<template>
  <div
    v-loading.fullscreen.lock="paymentFullScreenLoading"
    :element-loading-text="'Payment Processing...'"
    element-loading-spinner="el-icon-loading"
  >
    <div v-if="chargeAmount === 0 && useCoupon">
      <NewLibraryButton
        color-type="dark-blue"
        :class="isBottom ? 'bottom-fixed' : 'mt-7 w-full'"
        :loading="paymentWithNoChargeLoading"
        @click="completePaymentWithNoCharge"
      >
        Confirm
      </NewLibraryButton>
    </div>
    <div v-else>
      <!-- payment method: patient pay (now) -->
      <div v-if="paymentMethod === 'patientPayNow'" class="mt-5">
        <Alert v-if="showHsaWarning" type="warning" class="warning-alert mb-4">
          <div class="alert-content">
            <span>We can't process HSA/FSA payment for some items in your order. Please check with the patient and avoid using an HSA/FSA card for this purchase.</span>
          </div>
        </Alert>
        <StripePaymentForm 
          class="w-2/3"
          :sample-id="sampleId"
          :order-id="orderId"
          :charge-amount="chargeAmount"
          :account-id="patientId"
          :need-sample-id="needSampleId"
          :sample-list="sampleList"
          :stripe-account="stripeAccount"
          :clinic-id="clinicId"
          :currency="currency"
          :order-price-list="orderPriceList"
          :used-credit="usedCredit"
          :pay-order-without-apply="payOrderWithoutApply"
          :disabled="confirmDisabled"
          @completePayment="completePatientPayNowZb"
        />
      </div>

      <!-- payment method: customer pay -->
      <CustomerPay
        v-else-if="paymentMethod === 'customerPay'"
        class="mt-7"
        :charge-amount="chargeAmount"
        :charge-item-count="chargeItemsCount"
        :customer-credit="customerCredit"
        :net-pay-interval="netPayInterval"
        :is-clinic="isClinic"
        :customer-id="customerId"
        :is-show-ach-payment="isShowAchPayment"
        :sample-list="sampleList"
        :is-us-customer="isUsCustomer"
        :sample-id="sampleId"
        :order-id="orderId"
        :need-sample-id="needSampleId"
        :stripe-account="stripeAccount"
        :clinic-id="clinicId"
        :currency="currency"
        :order-price-list="orderPriceList"
        :pay-order-without-apply="payOrderWithoutApply"
        :used-credit="usedCredit"
        @completePayment="completeCustomerPay"
        @changeUseCustomerCredit="(value, amount) => $emit('changeUseCustomerCredit', value, amount)"
        @changeUseNetPay="$emit('changeUseNetPay', $event)"
      />
      <!-- payment method: patinet pay - later -->
      <PatientPayLater
        v-else-if="paymentMethod === 'patientPayLater'"
        class="my-3.5"
        :charge-item-count="chargeItemsCount"
        :disabled="confirmDisabled"
        @completePayment="completePatientPayLater"
      >
        <slot></slot>
      </PatientPayLater>
    </div>
    <Disclaimer 
      v-if="showDisclaimer"
      :button-text="disclaimerButtonText"
    /> 
  </div>
</template>

<script>
import StripePaymentForm from "./StripePaymentForm.vue";
import CustomerPay from "./CustomerPay/index.vue";
import PatientPayLater from "./PatientPayLater.vue";
import Disclaimer from "./Disclaimer/index.vue";
import Alert from '@/NewLibrary/Alert.vue';
import NewLibraryButton from "@/NewLibrary/Button.vue";
import { 
  saveChargePMToken, 
  deleteCreditCardById
} from "@/api/va-order-page/chargePage.js";
import { generateSampleIdsApi } from "@/api/va-order-page/orderTest.js";
import { notify } from '@/utils/notify';
import _ from "lodash";
import { modifyChargeMethodApi } from "@/api/va-order-page/patientPage";
import { payOrdersApi, chargeApi } from "@/api/va-order-page/newChargingService";
import { getChargeDetailsForPayOrdersApi, getOrderSamplePriceMaps } from ".";

export default {
  components: {
    StripePaymentForm,
    CustomerPay,
    PatientPayLater,
    Disclaimer,
    Alert,
    NewLibraryButton
  },
  props: {
    paymentMethod: {
      type: String,
      default: "customerPay"
    },

    patientId: {
      type: Number,
      default: null
    },
    chargeItemsCount: {
      type: Number,
      default: 0
    },
    chargeAmount: {
      type: Number,
      default: 0
    },
    customerCredit: {
      type: Number,
      default: 0
    },
    usedCredit: {
      type: Number,
      default: 0
    },
    netPayInterval: {
      type: Number,
      default: 0
    },
    customerId: {
      type: Number,
      default: null
    },
    isClinic: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    confirmDisabled: {
      type: Boolean,
      default: false
    },
    isPlanAndSave: {
      type: Boolean,
      default: false
    },
    orderPriceList: {
      type: Array,
      default: () => []
    },
    needSampleId: {
      type: Boolean,
      default: true
    },
    isShowAchPayment: {
      type: Boolean,
      default: true
    },
    // used for patient page pay bill
    sampleList: {
      type: Array,
      default: () => []
    },
    isUsCustomer: {
      type: Boolean,
      default: true
    },
    sampleId: {
      type: String,
      default: null
    },
    orderId: {
      type: String,
      default: null
    },
    stripeAccount: {
      type: String,
      default: ""
    },
    clinicId: {
      type: Number,
      default: null
    },
    currency: {
      type: Object,
      default: () => ({ name: "USD", symbol: "$"})
    },
    showHsaWarning: {
      type: Boolean,
      default: false
    },
    needModifyPaymentMethod: {
      type: Boolean,
      default: false
    },
    payOrderWithoutApply: {
      type: Boolean,
      default: false
    },
    useCoupon: {
      type: Boolean,
      default: false
    },
    showDisclaimer: {
      type: Boolean,
      default: true
    }
  },
  emits: [
    "completePatientPayNow", 
    "completeCustomerPay", 
    "completePatientPayLater",
    "changeUseCustomerCredit",
    "changeUseNetPay"
  ],
  data() {
    return {
      paymentFullScreenLoading: false,
      paymentWithNoChargeLoading: false,
      paymentId: null,
      orderSamplePriceMaps: {
        sampleIdJulienBarcodeMap: {},
        samplePriceMap: {}
      },
    }
  },
  computed: {
    disclaimerButtonText() {
      return this.paymentMethod === "patientPayLater" ? "Confirm" : "Complete Payment";
    },

    isBottom() {
      return document.location.href.includes('/ordertest') 
        && !document.location.href.includes('order-center');
    }
  },
  watch: {
    isLoading(newVal) {
      this.paymentFullScreenLoading = newVal;
    },
  },
  methods: {
    async completePatientPayNowZb(resultObj) {
      if (this.needModifyPaymentMethod) {
        await this.changeSamplePaymentMethod(this.sampleList, "patientPayNow");
      }
      this.$emit("completePatientPayNow", resultObj);
    },
    async completeCustomerPay(paymentInfo) {
      if (this.needModifyPaymentMethod) {
        await this.changeSamplePaymentMethod(this.sampleList, "customerPay");
      }
      if (paymentInfo.isAchPayment || paymentInfo.platform === "stripe") { // if ach payment, emit paymentInfo immediately
        paymentInfo.usedCredit = paymentInfo.isUseCustomerCredit ? this.usedCredit : null;
        this.$emit("completeCustomerPay", paymentInfo);
        return;
      }
      if (this.chargeItemsCount === 0) {
        notify({ type: 'error', message: 'Must order at least one test' });
        return; 
      }      
      let { isUseNetPay, isUseCustomerCredit, staxToken, isSaveCard } = paymentInfo;
      this.paymentFullScreenLoading = true;
      if (isUseNetPay) {
        let orderSamplePriceMaps = await getOrderSamplePriceMaps(this.orderPriceList);
        paymentInfo.orderSamplePriceMaps = orderSamplePriceMaps;
        this.$emit("completeCustomerPay", paymentInfo);
        return;
      }
      if (this.chargeAmount === 0 && isUseCustomerCredit) { // use customer credit & cover all charges
        let orderSamplePriceMaps = await getOrderSamplePriceMaps(this.orderPriceList);
        paymentInfo.orderSamplePriceMaps = orderSamplePriceMaps;
        paymentInfo.usedCredit = isUseCustomerCredit ? this.usedCredit : null;
        this.$emit("completeCustomerPay", paymentInfo);
        return;
      }
      // not call chargePMToken again if paymentId is already received
      try {
        if (!this.paymentId) {
          if (!staxToken.payment_token && this.chargeAmount !== 0) {
            this.paymentFullScreenLoading = false;
            let message = "Please add a credit card to complete payment.";
            notify({ type: 'error', message }); 
            return;
          }
          let samples = [];
          this.orderSamplePriceMaps = { sampleIdJulienBarcodeMap: {}, samplePriceMap: {} };
          if (this.sampleList.length > 0) {
            samples = this.sampleList;
          } else {
            let { sampleIdJulienBarcodeMap, samplePriceMap } = await getOrderSamplePriceMaps(this.orderPriceList);
            Object.keys(sampleIdJulienBarcodeMap).forEach(sampleId => {
              samples.push({
                orderId: null,
                sample_id: sampleId,
                balance: samplePriceMap[sampleId]
              });
            });
            this.orderSamplePriceMaps = { sampleIdJulienBarcodeMap, samplePriceMap };
          }
          let chargeResp = await this.chargeOrder(staxToken.payment_token, isSaveCard, "customerPay", samples, isUseCustomerCredit);
          if (!chargeResp) {
            this.paymentFullScreenLoading = false;
            return;
          }
        }
        paymentInfo.paymentId = this.paymentId;
        paymentInfo.usedCredit = isUseCustomerCredit ? this.usedCredit : null;
        paymentInfo.orderSamplePriceMaps = this.orderSamplePriceMaps;
        paymentInfo.amount = Number(this.chargeAmount.toFixed(2));
        paymentInfo.type = "card";
        paymentInfo.platform = "stax";
        this.$emit("completeCustomerPay", paymentInfo);
      } catch (err) {
        this.paymentFullScreenLoading = false;
      }
    },

    async completePatientPayLater() {
      this.paymentFullScreenLoading = true;
      this.$emit("completePatientPayLater");
    },
    async completePaymentWithNoCharge() {
      this.paymentWithNoChargeLoading = true;
      try {
        if (this.paymentMethod === "patientPayLater") {
          this.paymentWithNoChargeLoading = false;
          this.$emit("completePatientPayLater");
          return;
        }
        if (this.paymentMethod === "patientPayNow") {
          let orderSamplePriceMaps = await getOrderSamplePriceMaps(this.orderPriceList);
          let paymentInfo = {
            paymentId: null,
            platform: "stripe",
            orderSamplePriceMaps
          };
          this.paymentWithNoChargeLoading = false;
          this.$emit("completePatientPayNow", paymentInfo);
          return;
        }
        if (this.paymentMethod === "customerPay") {
          let orderSamplePriceMaps = await getOrderSamplePriceMaps(this.orderPriceList);
          const isUseCustomerCredit = this.usedCredit > 0;
          let usedCredit = this.usedCredit > 0 ? this.usedCredit : null;
          let paymentInfo = {
            paymentId: null,
            usedCredit,
            isUseCustomerCredit: usedCredit ? true : false,
            orderSamplePriceMaps
          };
          this.paymentWithNoChargeLoading = false;
          this.$emit("completeCustomerPay", paymentInfo);
          return;
        }
      } catch(err) {
        console.log("complete payment with no charge error", err);
        this.paymentWithNoChargeLoading = false;
      } 
    },

    async chargeOrder(paymentToken, isSaveCard, paymentMethod, sampleList, isUseCustomerCredit) {
      let chargeResp = await this.callPayOrdersApi(paymentMethod, paymentToken, sampleList, isUseCustomerCredit);
      if (!isSaveCard && paymentToken) {
        deleteCreditCardById(paymentToken);
      }
      return chargeResp;
    },

    async callPayOrdersApi(paymentMethod, paymentToken, sampleList, isUseCustomerCredit) {
      let accountId = null;
      let accountType = null;
      if (paymentMethod === "customerPay") {
        accountId = this.isClinic ? this.clinicId : this.customerId;
        accountType = this.isClinic ? "clinic" : "customer";
      }
      if (paymentMethod === "patientPayNow") {
        accountId = this.patientId;
        accountType = "patient";
      }
      let chargeDetails = getChargeDetailsForPayOrdersApi(sampleList, isUseCustomerCredit, this.customerCredit, "testorder", this.usedCredit);
      let totalAmount = this.chargeAmount;
      try {
        if (Array.isArray(sampleList) && sampleList.length > 0) {
          totalAmount = sampleList.reduce((sum, s) => {
            const val = (s.price !== undefined ? s.price : (s.balance !== undefined ? s.balance : 0));
            return sum + Number(val || 0);
          }, 0);
        }
      } catch (e) {
        totalAmount = this.chargeAmount;
      }
      try {
        let { data } = await payOrdersApi({
          account_id: accountId,
          account_type: accountType,
          amount: Number(Number(totalAmount).toFixed(2)),
          currency: this.currency.name.toLowerCase(),
          charge_details: chargeDetails,
          type: "card", 
          token_platform: "stax",
          payment_token: paymentToken,
          payment_source: "portal",
          without_apply: this.payOrderWithoutApply
        });
        this.paymentId = data.record_id;
        return data;
      } catch(err) {
        console.log("pay orders api error", err);
        this.paymentFullScreenLoading = false;
      }
    },

    async callChargeApi(paymentMethod, paymentToken, isSaveCard) {
      let accountId = null;
      let accountType = null;
      if (paymentMethod === "customerPay") {
        accountId = this.isClinic ? this.clinicId : this.customerId;
        accountType = this.isClinic ? "clinic" : "customer";
      }
      if (paymentMethod === "patientPayNow") {
        accountId = this.patientId;
        accountType = "patient";
      }
      let { data } = await chargeApi({
        account_id: accountId,
        account_type: accountType,
        amount: Number(this.chargeAmount.toFixed(2)),
        currency: this.currency.name.toLowerCase(),
        charge_type: "testorder",
        charge_type_id: this.orderId ? this.orderId : null,
        type: "card",
        token_platform: "stax",
        payment_source: "portal",
        payment_token: paymentToken,
        notes: "",
        new_sample: this.needSampleId,
        using_default: false,
        saving_for_later: isSaveCard,
        sample_id: this.sampleId ? Number(this.sampleId) : null,
      });
      this.paymentId = data.record_id;
      return data;
    },
    async changeSamplePaymentMethod(sampleList, newPayment) {
      let sampleIdsNeedEdit = sampleList.filter(sample => sample.paymentMethod !== newPayment).map(sample => sample.sample_id);
      if (sampleIdsNeedEdit.length === 0) {
        return;
      }
      try {
        let modifyChargeMethodResp = await modifyChargeMethodApi({
          sampleIdList: sampleIdsNeedEdit,
          updatedChargeMethod: newPayment
        });
      } catch(err) {
        console.log("change pay bill sample chargeMethod error", err);
        this.paymentFullScreenLoading = false;
      }
    }
  }
}
</script>

<style scoped>
.pay-text {
  height: 19px;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.46px;
  line-height: 19px;
}
.alert-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.alert-content span {
  color: var(--TrueBlack, #333);
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 300;
  line-height: 18px;
}
.bottom-fixed {
  position: fixed !important;
  bottom: 89px; 
  z-index: 1000; 
  width: 373px;
  height: 42px;
  right: 52px;
}
</style>